import { action, makeObservable, observable } from 'mobx';
import {
  freeBetStore,
  notificationStore,
  userInfoStore,
  applicationStore,
  betStore,
  gameConfigStore,
  roundInfoStore,
  userSettingsStore,
} from '@pascal/pascal-core';
import uuid from 'react-uuid';
import {
  autoBetDataKeys,
  autoCashOutDataKeys,
  betSectionTabsName,
  defaultMinOdd,
  minOdd,
} from '../enums';
import { gameLiveConnection, soundService } from '../services';
import { checkDoBetModel } from '../helpers/supporters';
import { marketsStore } from './marketsStore';
import { divide } from '../helpers/arithmeticOperations';
import { gameStore } from './gameStore';

class DoBetStore {
  betSectionTabName = betSectionTabsName.markets;

  totalBetsAmount = 0;

  currentBetAmount = null;

  winAmount = '';

  activeMaxPayoutPopData = {
    isShowMaxPayOutPopUp: false,
    maxPayout: null,
    isFirst: true,
  };

  autoBetData = {
    isAutoBet: false,
    isOpened: false,
    autoBetCount: 0,
  };

  autoCashOutData = {
    isOpened: false,
    isValidatedOdd: false,
    isBlurred: false,
    autoCashOutOdd: defaultMinOdd,
    autoCashOutMaxOdd: divide(
      gameConfigStore.limits.maxWin,
      gameConfigStore.limits?.defaultBetAmount || gameConfigStore.limits?.minBet
    ),
  };

  betData = {
    betId: null,
    amount: gameConfigStore.limits?.defaultBetAmount || gameConfigStore.limits?.minBet,
    isNextRoundBet: false,
    isBetAccepted: false,
    freeBetId: null,
    freeBetMinOdd: null,
    winAmount: null,
    finishedOdd: null,
    isDisabled: false,
  };

  terminalCashOutOddIndex = 0;

  reBetData = [];

  constructor() {
    makeObservable(this, {
      currentBetAmount: observable,
      terminalCashOutOddIndex: observable,
      reBetData: observable.deep,
      betData: observable.deep,
      betSectionTabName: observable,
      autoBetData: observable.deep,
      totalBetsAmount: observable,
      activeMaxPayoutPopData: observable.deep,
      winAmount: observable,
      autoCashOutData: observable,
      setBetAmount: action.bound,
      handleOnRoundStart: action.bound,
      updateBetAccepted: action.bound,
      updateAutoCashOutData: action.bound,
      doBetSender: action.bound,
      setReplayDataConfigs: action.bound,
      updateTotalBetsAmount: action.bound,
      sendCashOut: action.bound,
      setWinAmount: action.bound,
      setAutoCashOutOdd: action.bound,
      updateInitialBetData: action.bound,
      setFreeBetData: action.bound,
      resetDoBetData: action.bound,
      handleDoBetResult: action.bound,
      handleBetForNextRound: action.bound,
      handleClearRoundFin: action.bound,
      updateAutoBetData: action.bound,
      updateBetSectionTabName: action.bound,
      setActiveMaxPayoutPopUpData: action.bound,
      resetActiveMaxPayoutPopUpData: action.bound,
      updateIsDisabledBetSection: action.bound,
      resetGameStates: action.bound,
      setWinData: action.bound,
      resetAutoBet: action.bound,
      setIsTerminalAutoCashOut: action.bound,
      setTerminalCashOutOddIndex: action.bound,
      clearTerminalCashOutOddIndex: action.bound,
      setAutoCashOutData: action.bound,
      updatedReBetData: action.bound,
      setReBetData: action.bound,
      detectAndSetReBetData: action.bound,
      resetIsNextRoundBet: action.bound,
    });
  }

  setCurrentBetAmount(amount) {
    this.currentBetAmount = amount;
  }

  setTerminalCashOutOddIndex(index) {
    this.terminalCashOutOddIndex = index;
  }

  clearTerminalCashOutOddIndex() {
    this.terminalCashOutOddIndex = 0;
  }

  setActiveMaxPayoutPopUpData(isShowMaxPayOutPopUp, maxPayout, isFirst) {
    this.activeMaxPayoutPopData = { maxPayout, isFirst, isShowMaxPayOutPopUp };
  }

  resetActiveMaxPayoutPopUpData(isReset, isFirst) {
    this.activeMaxPayoutPopData = isReset
      ? {
          isShowMaxPayOutPopUp: false,
          maxPayout: null,
          isFirst: true,
        }
      : { ...this.activeMaxPayoutPopData, isFirst };
  }

  setIsTerminalAutoCashOut() {
    this.autoCashOutData.isOpened = !this.autoCashOutData.isOpened;
  }

  setFreeBetData(freeBetMinOdd, freeBetId = null, freeBetMaxPayout = null, freeBetType = null) {
    this.betData = {
      ...this.betData,
      freeBetId,
      freeBetType,
      freeBetMinOdd,
      freeBetMaxPayout,
    };
  }

  setWinAmount(winAmount) {
    this.winAmount = winAmount;
  }

  updateTotalBetsAmount(amount, isReset = false) {
    if (isReset) {
      this.totalBetsAmount = 0;
    } else {
      this.totalBetsAmount += Number(amount);
    }
  }

  updateAutoBetData(key, value) {
    this.autoBetData = {
      ...this.autoBetData,
      [key]: value,
    };
  }

  resetAutoBet() {
    this.autoBetData = {
      isAutoBet: false,
      isOpened: false,
      autoBetCount: 0,
    };
  }

  updateBetSectionTabName(betSectionTabName) {
    this.betSectionTabName = betSectionTabName;
  }

  updateIsDisabledBetSection(isDisabledBetSection) {
    this.betData = {
      ...this.betData,
      isDisabled: isDisabledBetSection,
    };
  }

  setBetAmount(amount) {
    this.betData = { ...this.betData, amount };
  }

  resetDoBetData() {
    this.betData = {
      ...this.betData,
      amount: gameConfigStore.limits.defaultBetAmount || gameConfigStore.limits.minBet || 0,
      isNextRoundBet: false,
      isBetAccepted: false,
    };
    this.autoCashOutData = {
      isOpened: false,
      autoCashOutOdd: defaultMinOdd,
    };
  }

  updateBetAccepted(isBetAccepted, betId) {
    this.betData = {
      ...this.betData,
      isBetAccepted,
      betId,
    };
  }

  setAutoCashOutData(value) {
    this.autoCashOutData.autoCashOutOdd = value;
  }

  setAutoCashOutOdd(autoCashOutOdd) {
    this.autoCashOutData = {
      ...this.autoCashOutData,
      autoCashOutOdd,
    };
  }

  updateAutoCashOutData(key, value) {
    const { activeFreeBet } = freeBetStore;
    this.autoCashOutData = {
      ...this.autoCashOutData,
      ...(key === autoCashOutDataKeys.isOpened && !this.autoCashOutData.isOpened
        ? { autoCashOutOdd: activeFreeBet?.id ? activeFreeBet?.minOdd : defaultMinOdd }
        : {}),
      [key]: value,
    };
  }

  updateInitialBetData(data) {
    this.betData = data;
  }

  doBetSender(isMarketSection = false) {
    const { limits } = gameConfigStore;
    const { balance: userBalance } = userInfoStore;
    const { amount } = this.betData;
    const { roundId } = roundInfoStore;
    const { gameTypeId } = gameConfigStore;
    const { isDemo } = applicationStore;
    const { isAutoBet } = this.autoBetData;
    const { activeFreeBet } = freeBetStore;
    const {
      currentMarketAmount,
      marketBetData: { market },
    } = marketsStore;

    const currentAmount = isMarketSection ? currentMarketAmount : amount; // todo check user amount
    const responseCode = checkDoBetModel(limits, userBalance, currentAmount);

    !isMarketSection && this.updateIsDisabledBetSection(true);
    isMarketSection && marketsStore.setIsLoadingBetButton(true);
    if (responseCode) {
      notificationStore.setNotificationType({
        responseCode,
      });

      if (isAutoBet) {
        this.resetAutoBet();
      }
      isMarketSection && marketsStore.setIsLoadingBetButton(false);
      !isMarketSection && this.updateIsDisabledBetSection(false);
    } else {
      const doBetModel = {
        amount,
        roundId: roundId,
        gameTypeId,
        freeBetId: activeFreeBet?.id && !isDemo ? activeFreeBet?.id : null,
        betData: {
          amount: this.betData.amount,
          autoCashoutOdd: this.autoCashOutData.isOpened ? this.autoCashOutData.autoCashOutOdd : 0,
          isManual: true,
        },
      };

      const marketBetData = {
        amount: currentMarketAmount,
        roundId: roundId,
        gameTypeId,
        freeBetId: activeFreeBet?.id && !isDemo ? activeFreeBet?.id : null,
        betData: {
          amount: currentMarketAmount,
          markets: [
            {
              ...market,
              id: uuid(),
              amount: currentMarketAmount,
            },
          ],
          isManual: false,
        },
      };
      if (isMarketSection) {
        marketsStore.updateDisabledMarkets(market.market);
        marketsStore.updateIsDisabledMarketBetButton(false);
      }
      // if (isDemo) {
      //   const currentDemoBetData = {
      //     ...(isMarketSection ? marketBetData : doBetModel),
      //     status: BetStatus.Pending,
      //     id: createGuid(),
      //     date: new Date(),
      //     responseCode: BetResponseCode.Success,
      //     isManual: isMarketSection,
      //   };
      //   if (currentDemoBetData.amount <= userInfoStore.balance) {
      //     this.decrementBalance(isMarketSection ? currentMarketAmount : amount);
      //     this.updateTotalBetsAmount(isMarketSection ? currentMarketAmount : amount);
      //     this.handleDoBetResult(currentDemoBetData);
      //     popupStore.updateDemoBetsCount();
      //   }
      // } else {
      gameLiveConnection.doBet(isMarketSection ? marketBetData : doBetModel);
      // }
    }
  }

  handleOnRoundStart() {
    const { isAutoBet, autoBetCount } = this.autoBetData;

    this.betData = {
      ...this.betData,
      isBetAccepted: false,
      isDisabled: false,
    };
    if (this.betData.isNextRoundBet) {
      this.doBetSender();
    }

    if (marketsStore.marketBetData.isMarketNextRoundBet) {
      this.doBetSender(true);
      marketsStore.setIsNexRoundBet(false);
    }

    if (isAutoBet && autoBetCount) {
      !this.betData.isDisabled && this.updateIsDisabledBetSection(true);
      this.doBetSender();
    }
  }

  resetIsNextRoundBet() {
    this.betData = {
      ...this.betData,
      isNextRoundBet: false,
    };
    marketsStore.updateIsDisabledMarketBetButton(false);
  }

  setReplayDataConfigs() {
    const { repliedBetData } = betStore;
    if (repliedBetData?.replayData?.markets) {
      marketsStore.getMarkets(repliedBetData?.replayData?.markets);
    }
    this.updateBetSectionTabName(
      repliedBetData?.additionalParams?.isManual
        ? betSectionTabsName.manual
        : betSectionTabsName?.markets
    );
    if (repliedBetData?.additionalParams?.autoCashOutOdd) {
      this.autoCashOutData = {
        isOpened: true,
        isValidatedOdd: true,
        isBlurred: true,
        autoCashOutOdd: repliedBetData?.additionalParams?.autoCashOutOdd,
        autoCashOutMaxOdd: null,
      };
    }
    this.setBetAmount(repliedBetData?.amount);
    marketsStore.changeCurrentMarketAmount(repliedBetData?.amount);
  }

  handleBetForNextRound() {
    this.betData = {
      ...this.betData,
      isNextRoundBet: !this.betData.isNextRoundBet,
    };
  }

  resetGameStates(isPlayForReal = false) {
    this.betData = {
      betId: null,
      amount: gameConfigStore.limits?.defaultBetAmount || gameConfigStore.limits.minBet,
      isNextRoundBet: false,
      isBetAccepted: false,
      winAmount: null,
      finishedOdd: null,
      isDisabled: false,
    };
    this.autoBetData = {
      isAutoBet: false,
      isOpened: false,
      autoBetCount: 0,
    };
    !isPlayForReal && gameStore.setOddData(null);
    this.autoCashOutData = {
      isOpened: false,
      autoCashOutOdd: minOdd,
    };
    marketsStore.setCurrentClickedMarket('');
    marketsStore.setTotalWin('');
    this.setWinAmount('');
    this.updateTotalBetsAmount(0, true);
    notificationStore.setNotificationType(null);
  }

  handleDoBetResult(doBetData) {
    const {
      responseCode,
      id,
      amount,
      betData: { autoCashoutOdd, isManual },
    } = doBetData;
    const { isAutoBet, autoBetCount } = this.autoBetData;
    this.setCurrentBetAmount(amount);

    if (isManual) {
      this.updateBetAccepted(true, id);
      const { isOpened } = this.autoCashOutData;
      if (isOpened && autoCashoutOdd) {
        this.updateAutoCashOutData(autoCashOutDataKeys.autoCashOutOdd, autoCashoutOdd);
      }
    } else {
      const isReBet = doBetData.betData.markets.some((item) => item.isReBet);
      if (!isReBet && this.reBetData.length) {
        const { liveBets } = betStore;
        const hasBets = liveBets.some((item) => item.userId === userInfoStore.userId);

        if (!hasBets) {
          this.setReBetData([]);
        }
      }
    }
    // if (isDemo) {
    //   betStore.updateLiveBets(doBetData);
    //   marketsStore.setBetsMarketData(doBetData);
    //   this.betData = {
    //     ...this.betData,
    //     isNextRoundBet: false,
    //   };
    // }

    if (!isManual) {
      marketsStore.setIsLoadingBetButton(false);
      marketsStore.setIsLoadingReBetButton(false);
    }
    notificationStore.setNotificationType({ responseCode });

    if (this.betData.isBetAccepted && isAutoBet && autoBetCount && isManual) {
      this.updateAutoBetData(autoBetDataKeys.autoBetCount, autoBetCount - 1);
    } else if (!autoBetCount) {
      this.updateAutoBetData(autoBetDataKeys.isAutoBet, false);
    }
  }

  setWinData(data) {
    const { winAmount } = data;
    const { isSoundOn } = userSettingsStore;
    this.betData = {
      ...this.betData,
      isBetAccepted: false,
      winAmount,
    };
    if (isSoundOn) {
      soundService.playWin();
    }
    // isDemo ? multiply(this.betData.amount, gameStore.oddData) :
    this.setWinAmount(winAmount);
    // if (isDemo) {
    //   this.incrementDemoBalance(this.betData.amount, gameStore.oddData);
    // } else {
    setTimeout(gameLiveConnection.getBalance, 500);
    // }
  }

  sendCashOut(betId) {
    // const { isDemo } = applicationStore;
    // const { oddData } = gameStore;
    // const { roundId } = roundInfoStore;
    // const { Win } = BetStatus;
    const { isAutoBet, autoBetCount } = this.autoBetData;
    this.updateBetAccepted(false, betId);
    if (isAutoBet && !autoBetCount) {
      this.updateAutoBetData(autoBetDataKeys.isAutoBet, false);
      this.updateAutoBetData(autoBetDataKeys.autoBetCount, 0);
      this.updateAutoBetData(autoBetDataKeys.isOpened, false);
    }
    // if (isDemo) {
    //   const demoBetData = {
    //     ...this.betData,
    //     id: betId,
    //     status: Win,
    //     roundId: roundId,
    //     winAmount: parseNumberByPrecision(this.betData.amount, oddData, 2),
    //     date: new Date(),
    //     odd: oddData,
    //     additionalParams: {
    //       autoCashOutOdd: this.autoCashOutData.autoCashOutOdd,
    //     },
    //   };
    //   this.setWinData(demoBetData);
    //   betStore.updateDemoBetHistory([demoBetData]);
    //   const updatedLiveBets = betStore.liveBets.map((item) =>
    //     item.id === demoBetData.id
    //       ? {
    //           ...item,
    //           ...demoBetData,
    //           winAmount: Number(demoBetData.winAmount),
    //           status: demoBetData.status,
    //           odd: demoBetData?.odd,
    //         }
    //       : item
    //   );
    //   if (isAutoBet && autoBetCount) {
    //     this.updateIsDisabledBetSection(true);
    //   }
    //   betStore.setLiveBets(updatedLiveBets);
    // } else {
    gameLiveConnection.cashOut({ betId, roundId: roundInfoStore.roundId });
    // }
  }

  handleClearRoundFin() {
    const { isAutoBet, autoBetCount } = this.autoBetData;
    gameStore.setOddData(null);
    this.betData = {
      ...this.betData,
      betId: null,
      isBetAccepted: false,
      isDisabled:
        isAutoBet && autoBetCount && !applicationStore.isDemo ? true : this.betData.isNextRoundBet,
    };
  }

  updatedReBetData(betData) {
    this.reBetData = [...this.reBetData, betData];
  }

  setReBetData(bets) {
    this.reBetData = bets;
  }

  detectAndSetReBetData(data, isFromFreeBetToggle) {
    if (data.length) {
      const { activeFreeBet } = freeBetStore;
      const bets = data.filter((bet) => {
        if (!activeFreeBet?.id && bet.freeBetId) {
          return false;
        }

        if (activeFreeBet?.id) {
          if (!bet.freeBetId) {
            return false;
          }

          return bet.freeBetId === activeFreeBet.id;
        }

        if (bet.additionalParams) {
          return !bet.additionalParams.isManual;
        }

        return bet.market;
      });

      if (isFromFreeBetToggle) {
        this.setReBetData(bets);
      } else {
        const betsForParse = bets.map((bet) => {
          if (bet.additionalParams.markets.length && this.reBetData.length) {
            this.setReBetData([]);
          }

          return bet;
        });

        betsForParse.forEach((betForParse) => {
          if (betForParse && betForParse.additionalParams.markets.length) {
            betForParse.additionalParams.markets.forEach((item) => {
              this.updatedReBetData({
                ...item,
                userId: betForParse.userId,
                freeBetId: betForParse.freeBetId,
                currencyCode: betForParse.currencyCode,
                playerExternalId: betForParse.playerExternalId,
              });
            });
          }
        });
      }
    }
  }

  handleReBetClickAction = () => {
    const { roundId } = roundInfoStore;
    const { gameTypeId } = gameConfigStore;
    const { activeFreeBet } = freeBetStore;
    const { isDemo } = applicationStore;
    marketsStore.setIsLoadingReBetButton(true);

    const reBetModel = {
      roundId,
      gameTypeId,
      amount: this.reBetData.reduce((acc, curr) => acc + curr.amount, 0),
      freeBetId: activeFreeBet?.id && !isDemo ? activeFreeBet?.id : null,
      betData: {
        markets: this.reBetData.map((el) => {
          return {
            isReBet: true,
            amount: el.amount,
            market: el.market,
            id: uuid(),
            freeBetId: activeFreeBet?.id && !isDemo ? activeFreeBet?.id : null,
          };
        }),
      },
    };
    gameLiveConnection.doBet(reBetModel);
  };
}

export const doBetStore = new DoBetStore();
